import { createContext, useState, useCallback, useContext } from "react";
import { AuthContext } from "./auth-context";
import { useHttpClient } from "../hooks/http-hook";
import { useActiveDoctors } from "./active-doctors-context";

export const NotificationsContext = createContext({
  notifications: [],
  fetchNotifications: () => {},
  addNotification: () => {},
  removeNotification: () => {},
  clearAllNotifications: () => {},
});

export const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);
  const { activeDoctorIds } = useActiveDoctors();

  // Fetch notifications from the server
  const fetchNotifications = useCallback(
    async (clinicaId) => {
      if (!auth.token || !clinicaId) return;

      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/notificacoes/clinica/${clinicaId}/${auth.userId}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          }
        );

        // Process notifications if available
        if (responseData.notificacoes && responseData.notificacoes.length > 0) {
          let processedNotifications = responseData.notificacoes.map((n) => ({
            id: n._id,
            tipo: n.tipo,
            mensagem: n.mensagem,
            cliente: n.cliente,
            dados: n.dados,
            medicoId: n.medicoId,
            medicoNome: n.medicoNome || "Médico",
            created_at: n.created_at,
          }));

          // Filter notifications by active doctors if user is assistant or secretary
          if (
            ["Assistente", "Secretária(o)"].includes(auth.role) &&
            activeDoctorIds
          ) {
            processedNotifications = processedNotifications.filter(
              (notification) =>
                // Include notifications without a medicoId and those from active doctors
                !notification.medicoId ||
                activeDoctorIds.includes(notification.medicoId) ||
                /* Os documentos para imprimir nao passam por este filtro: ele
                   existe para os avisos de avaliacao, que so interessam a quem
                   esta a trabalhar com aquele medico. Imprimir e de quem esta
                   ao balcao, seja de que medico for o papel. A mesma isencao
                   existe no ramo do websocket, no `Navbar` -- as duas tem de
                   concordar, senao o aviso aparece ao vivo e desaparece ao
                   recarregar a pagina. */
                notification.tipo === "documentoParaImprimir"
            );
          }

          setNotifications(processedNotifications);
        }
      } catch (err) {
        console.error("Error fetching notifications:", err);
      }
    },
    [auth, sendRequest]
  );

  const addNotification = useCallback((notification) => {
    setNotifications((current) => [...current, notification]);
  }, []);

  /* Tira a notificacao da lista SEM chamar o servidor.
   *
   * Serve os avisos que sao tarefas, resolvidos por outra pessoa: o servidor ja
   * os marcou como resolvidos e avisou-nos pelo websocket. Chamar o
   * `removeNotification` aqui mandava um segundo pedido a dizer ao servidor
   * algo que foi ele a dizer-nos -- e em cada sino aberto da clinica. */
  const descartarLocalmente = useCallback((notificationId) => {
    setNotifications((current) =>
      current.filter((notification) => notification.id !== notificationId)
    );
  }, []);

  const removeNotification = useCallback(
    async (notificationId, userId) => {
      const formData = new FormData();
      formData.append("userId", userId);
      try {
        await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/notificacoes/${notificationId}`,
          "PATCH",
          formData,
          {
            Authorization: "Bearer " + auth.token,
          }
        );
        setNotifications((current) =>
          current.filter((notification) => notification.id !== notificationId)
        );
      } catch (err) {
        console.error("Error removing notification:", err);
      }
    },
    [auth.token, sendRequest]
  );

  const clearAllNotifications = useCallback(
    async (userId) => {
      try {
        await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/notificacoes/todas/${userId}`,
          "DELETE",
          {},
          {
            Authorization: "Bearer " + auth.token,
          }
        );
        setNotifications([]);
      } catch (err) {
        console.error("Error clearing notifications:", err);
      }
    },
    [auth.userId, auth.token, sendRequest]
  );

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        fetchNotifications,
        addNotification,
        removeNotification,
        descartarLocalmente,
        clearAllNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
